/* global Vue, LOYALTY_API, LOYALTY_LEVELS, LOYALTY_AWARDS */

import Modal from '../../apps/components/Modal'
import CldImg from '../../apps/components/CldImg'
import { setErrorAndStopLoading, startLoading, stopLoading } from '../../apps/components/Notifications'
import Reservations from '../components/Reservations'
import LevelsProgress from '../components/LevelsProgress'
import MemberCard from '../components/MemberCard'
import MaiFamilyCard from '../components/MaiFamilyCard'
import PropertyCard from '../../apps/components/PropertyCard'
import { BookingParams } from '../../classes/BookingParams'
import SectionHeading from '../components/SectionHeading'
import { AuthService, fetchWithTokenAsync } from '../../AuthService'
import AwardCardList from '../components/AwardCardList'
import { LoyaltyProfile, useLoyaltyProfile } from '../components/LoyaltyProfile'
import Interests from './Interests'
import Preferences from './Preferences'

export default {
  name: 'Dashboard',
  components: { Modal, Reservations, LevelsProgress, SectionHeading, MemberCard, MaiFamilyCard, PropertyCard, AwardCardList, CldImg, LoyaltyProfile, Interests, Preferences },
  template: `
<div class="hero">
  <div class="swiper">
    <div class="swiper-wrapper">
      <div class="swiper-slide">
        <CldImg :src="maiFamily ? 'https://res.cloudinary.com/maistra/image/upload/v1793613457/Loyalty/maifamily-dashboard-hero_ujw2c4.png' : 'https://res.cloudinary.com/maistra/image/upload/v1637157010/Loyalty/loyalty-dashboard-hero.jpg'" alt="" class="hero__poster" width="1440" height="900" />

        <div class="container grid">
          <div class="hero__content col-11 col-xs-10">
            <p class="hero__supertitle">{{ $t('dashboard.welcome') }}</p>
            <h1 class="hero-title" data-sl="mask">{{ dashboard.displayName }}</h1>
            <hr>

            <div class="hero__description">
              <p class="h4 no-margin">
                <span v-if="memberLevelName">{{ $t('dashboard.level')}}: {{ memberLevelName }}</span>
                <span>{{ $t('dashboard.membershipNumber') }}: {{ dashboard.loyaltyId }}</span>
              </p>
            </div>

            <p v-if="maiFamily" class="no-margin-bottom"><a href="#" role="button" @click.prevent="scrollToForm">{{ $t('dashboard.editprofile') }}</a></p>
            <p v-else class="no-margin-bottom"><button class="button button--outline" @click="scrollToForm">{{ $t('dashboard.editprofile') }}</button></p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <button class="scroll-down scroll-down--hero" aria-hidden="true" tabindex="-1">
    <i class="icon-arrow-down"></i>
    <span>{{ $t('dashboard.scrollDown') }}</span>
  </button>
</div>

<div class="container">
  <MaiFamilyCard v-if="maiFamily" v-bind="maiFamily" />
  <MemberCard
    v-else
    :loyaltyId="dashboard.loyaltyId"
    :memberName="dashboard.displayName"
    :memberSince="dashboard.memberSince"
    :qrCode="dashboard.qrCode"
    @createApplePass="createApplePass"
    @createGooglePass="createGooglePass"
    @enlargeQr="isModalOpen = true" />
</div>

<Modal v-if="!maiFamily" :isOpen="isModalOpen" @closeModal="isModalOpen = false" :title="dashboard.displayName" class="qr-modal">
  <p class="no-margin">{{ $t('dashboard.level') }}: {{ memberLevelName }}</p>
  <p class="no-margin">{{ $t('dashboard.membershipNumber') }}: {{ dashboard.loyaltyId }}</p>
  <img :src="dashboard.qrCode" width="400" data-sl="mask" />
</Modal>

<div v-if="dashboard.availablePoints >= 0 && !$AuthService.isMaiFamily" class="container">
  <div class="grid">
    <div class="col-12 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
      <LevelsProgress
        :availablePoints="dashboard.availablePoints"
        :expiresOn="dashboard.expiresOn"
        :level="dashboard.level"
        :proceed="userLoyaltyLevel?.proceed" />
    </div>
  </div>
</div>

<div class="bookings container grid">
  <div class="col-12 col-lg-10 offset-lg-1 col-xxl-8 offset-xxl-2">
    <div class="grid no-row-gap section-margin-small align-items-center">
      <h2 class="no-margin col-12 col-xs-6 col-sm-7 col-md-8">{{ $t('dashboard.myBookings') }}</h2>

      <div class="col-6 col-sm-5 col-md-4 text-right hide-xs-down">
        <router-link class="arrow-button" to="/reservations/past">
          <span>{{ $t('dashboard.viewBookings') }}</span>
          <i class="icon-arrow-right"></i>
        </router-link>
      </div>
    </div>

    <Reservations
      :active="true"
      :limit="parseInt(4)"
      :loyaltyId="loyaltyId"
      :properties="properties"
      @error="e => $emit('error', e)" />
  </div>
</div>

<div v-if="maiFamilyProperties.length" class="container grid">
  <div class="col-12 col-lg-10 offset-lg-1 col-xxl-8 offset-xxl-2">
    <div class="grid no-row-gap section-margin-small align-items-center">
      <h2 class="no-margin col-12 col-xs-6 col-sm-7 col-md-8">{{ $t('dashboard.maiFamilyProperties') }}</h2>

      <div v-if="maiFamily.propertiesUrl" class="col-6 col-sm-5 col-md-4 text-right hide-xs-down">
        <a class="arrow-button" :href="maiFamily.propertiesUrl">
          <span>{{ $t('dashboard.viewProperties') }}</span>
          <i class="icon-arrow-right"></i>
        </a>
      </div>
    </div>

    <PropertyCard
      v-for="property in maiFamilyProperties"
      :key="property.link"
      :property="property"
      :bookingParams="bookingParams"
      :hasPrice="false"
      :showGallery="true"
      class="negative-cp-xs-down"
      :fallbackAvailability="true" />
  </div>
</div>

<hr class="section-margin-top" ref="editForm">

<div class="tabs tabs--auto-height" v-show="loyaltyId">
  <div class="container grid">
    <div class="col-12 col-lg-10 offset-lg-1 col-xxl-8 offset-xxl-2">
      <h2 class="section-margin-small-top">{{ $t('myProfile.title') }}</h2>
      <div class="tabs__thumbs swiper">
        <div class="swiper-wrapper">
          <div class="swiper-slide">
            <span>{{ $t('dashboard.personalDetails') }}</span>
          </div>
          <div class="swiper-slide">
            <span>{{ $t('interests') }}</span>
          </div>
          <div class="swiper-slide">
            <span>{{ $t('preferencesTab.title') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="tabs__items swiper">
    <div class="swiper-wrapper">
      <div class="swiper-slide">
        <LoyaltyProfile :profile="profile" />
      </div>
      <div class="swiper-slide">
        <Interests :loyaltyId="loyaltyId" />
      </div>
      <div class="swiper-slide">
        <Preferences :loyaltyId="loyaltyId" />
      </div>
    </div>
  </div>
</div>

<div class="container grid no-padding-sm-down section-margin-bottom">
  <div class="col-12 col-lg-10 offset-lg-1 col-xxl-8 offset-xxl-2 note">
    <p v-html="$t('interestsTab.dataProtection')" />
  </div>
</div>
  `,
  emits: ['error'],
  props: {
    loyaltyId: Number,
    properties: Array,
  },
  setup (props, { emit }) {
    const dashboard = Vue.reactive({
      displayName: '',
      level: 0,
      loyaltyId: 0,
      memberSince: null,
      qrCode: '',
      points: 0,
    })

    const awards = Vue.ref([])
    const isModalOpen = Vue.ref(false)
    const editForm = Vue.ref(null)
    const { loyaltyId } = Vue.toRefs(props)
    const { profile } = useLoyaltyProfile()
    const maiFamily = Vue.inject('maiFamily', null)

    const maiFamilyProperties = Vue.computed(() => maiFamily ? props.properties.filter(p => p.isMaiFamilySuggestion).slice(0, 4) : [])
    const bookingParams = Vue.reactive(new BookingParams({ loginHint: AuthService.loginHint ?? '' }))

    const userLoyaltyLevel = Vue.computed(() => LOYALTY_LEVELS.find(level => level.level === dashboard.level))

    const memberLevelName = Vue.computed(() => {
      if (AuthService.isMaiFamily) return 'MaiFamily'
      return (LOYALTY_LEVELS.find(l => l.level === dashboard.level))?.title
    })

    function scrollToForm () {
      editForm.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    function download (filename, url) {
      const element = document.createElement('a')
      const parent = document.querySelector('.loyalty-member-card')
      element.setAttribute('href', url)
      element.setAttribute('download', filename)
      element.innerText = 'Download Maistra.pkpass'
      parent.appendChild(element)
      element.style.display = 'none'
      element.click()
      parent.removeChild(element)
    }

    async function createApplePass () {
      startLoading('createApplePass')
      try {
        const response = await fetch('/.netlify/functions/create-apple-pass', {
          method: 'POST',
          body: JSON.stringify({ ...dashboard, levelName: memberLevelName.value }),
        })
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        download('Maistra.pkpass', url)
        setTimeout(() => URL.revokeObjectURL(url), 10000)
        stopLoading('createApplePass')
      } catch (err) {
        setErrorAndStopLoading(err, true)
      }
    }

    async function createGooglePass () {
      startLoading('createGooglePass')
      try {
        const response = await fetch('/.netlify/functions/create-google-pass', {
          method: 'POST',
          body: JSON.stringify({ ...dashboard, levelName: memberLevelName.value }),
        })
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        const url = await response.text()
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.click()
        stopLoading('createGooglePass')
      } catch (err) {
        setErrorAndStopLoading(err, true)
      }
    }

    Vue.watch(loyaltyId, async () => {
      try {
        startLoading('awards-watch')
        const responseAwards = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/getawards/1/1`, AuthService.accessToken, 'GET')
        if (!responseAwards?.awards) {
          throw Error('awards')
        }
        const beternaAwards = responseAwards.awards
        awards.value = beternaAwards.filter(ba => {
          return LOYALTY_AWARDS.some(ca => {
            if (ca.beternaid === ba.prizeID.toString()) {
              Object.assign(ba, ca)
              return true
            }
            return false
          })
        })
        stopLoading('awards-watch')
      } catch (e) {
        setErrorAndStopLoading(e)
      }
    }, { immediate: true })

    Vue.watch(loyaltyId, async () => {
      try {
        startLoading('dashboard-watch')
        dashboard.loyaltyId = props.loyaltyId
        dashboard.displayName = AuthService.getDisplayName()

        // MaiFamily card has no QR code
        if (!maiFamily) {
          const responseQrCode = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/loyaltyprofiles/MyLoyaltyQrCode`, AuthService.accessToken, 'GET')
          if (!responseQrCode?.value || responseQrCode.value.length < 5) {
            throw Error('MyLoyaltyQrCode')
          }
          dashboard.qrCode = `data:image/jpeg;base64,${responseQrCode.value}`
        }

        const responseLoyaltyStatus = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/MyLoyaltyStatus`, AuthService.accessToken, 'GET')
        if (!responseLoyaltyStatus?.loyaltyProfile) {
          throw Error('MyLoyaltyStatus')
        }
        dashboard.memberSince = new Date(responseLoyaltyStatus.loyaltyProfile.createdOn)
        dashboard.level = responseLoyaltyStatus.loyaltyProfile.levelId
        dashboard.availablePoints = responseLoyaltyStatus.loyaltyProfile.availablePoints
        dashboard.expiresOn = new Date(responseLoyaltyStatus.loyaltyProfile.expiresOn)
        const responseAwards = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/getawards/1/1`, AuthService.accessToken, 'GET')
        if (!responseAwards?.awards) throw Error('awards')
        stopLoading('dashboard-watch')
      } catch (e) {
        setErrorAndStopLoading(e)
      }
    }, { immediate: true })

    return {
      dashboard,
      profile,
      maiFamily,
      maiFamilyProperties,
      bookingParams,
      createApplePass,
      createGooglePass,
      awards,
      isModalOpen,
      memberLevelName,
      LOYALTY_LEVELS,
      userLoyaltyLevel,
      editForm,
      scrollToForm,
    }
  },
}
